// ============================================
// Save Progress Banner
//
// Shown inside PatientChatEngine after 3+ messages when
// the patient is still anonymous. Offers compact
// inline account creation so progress persists
// across browsers and devices.
//
// On account creation, links the current journey
// to the new UID so it shows in My Results.
// ============================================

import { useState, useEffect } from 'react';
import { Shield, X } from 'lucide-react';
import { PatientAuthForm } from '../patient/PatientAuthForm';
import {
  getCurrentPatientAuth,
  onPatientAuthStateChanged,
} from '../../services/auth/authUpgradeService';
import { linkJourneyToUid } from '../../services/firestore/journeyService';
import { getSessionId } from '../../utils/sessionId';

interface SaveProgressBannerProps {
  messageCount: number;
}

export function SaveProgressBanner({ messageCount }: SaveProgressBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const auth = getCurrentPatientAuth();
    setIsAuthenticated(auth.isAuthenticated);

    const unsub = onPatientAuthStateChanged((state) => {
      setIsAuthenticated(state.isAuthenticated);
    });
    return unsub;
  }, []);

  // Don't show if: already authenticated, dismissed, or not enough messages
  if (isAuthenticated || dismissed || messageCount < 3) return null;

  function handleSuccess(uid: string, _email: string) {
    // Link the current journey to the newly created account
    const sessionId = getSessionId();
    if (!sessionId.startsWith('ephemeral_')) {
      linkJourneyToUid(sessionId, uid).catch((err) =>
        console.warn('[SaveProgress] Journey link failed:', err),
      );
    }
    setIsAuthenticated(true);
  }

  if (showForm) {
    return (
      <div className="mx-4 my-3 p-4 bg-white border border-[#E4E4E7] rounded-[4px]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[14px] font-medium text-[#18181B]">Save your progress</span>
          <button
            onClick={() => setShowForm(false)}
            className="text-[#A1A1AA] hover:text-[#52525B] transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <PatientAuthForm
          compact
          initialMode="register"
          registerHeading="Create your account"
          registerSubtext="Access your results from any device."
          onSuccess={handleSuccess}
          onSkip={() => setDismissed(true)}
        />
      </div>
    );
  }

  return (
    <div className="mx-4 my-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="flex items-center gap-3 p-3 bg-[#FFF1F2] border border-[#FECDD3] rounded-[4px]">
        <Shield size={16} className="text-[#E11D48] flex-shrink-0" />
        <p className="text-[13px] text-[#18181B] flex-1">
          <button
            onClick={() => setShowForm(true)}
            className="font-medium text-[#E11D48] hover:text-[#BE123C] transition-colors underline underline-offset-2"
          >
            Create a free account
          </button>
          {' '}to save your progress and come back anytime.
        </p>
        <button
          onClick={() => setDismissed(true)}
          className="text-[#A1A1AA] hover:text-[#52525B] transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
